import { SkillProposalReview } from "./SkillProposalReview";
import { t } from "../../i18n";
import {
  BarChart3,
  CheckCircle2,
  CircleAlert,
  FileCheck2,
  Mail,
  Send,
  Settings2,
  UserRound,
} from "lucide-react";
import { asRecord } from "./ToolResultView.utils";
import { CopyButton } from "../CopyButton";
import { GenericResult } from "./GenericResult";
import { text, titleCase } from "./OperationResult.utils";
import { ActionLink } from "./OperationResultActionLink";
import { BusinessRows } from "./OperationResultBusinessRows";
import { Card } from "./OperationResultCard";
import { Heading } from "./OperationResultHeading";

export function OperationResult({
  name,
  argumentsValue,
  result,
  status,
}: {
  name: string;
  argumentsValue: unknown;
  result: unknown;
  status?: string;
}) {
  const values = asRecord(result);
  const input = asRecord(argumentsValue) ?? {};
  if (!values) {
    if (name === "manage_business_integration" && Array.isArray(result))
      return (
        <BusinessRows operation={text(input.operation)} entries={result} />
      );
    return <GenericResult value={result} />;
  }

  if (name === "request_business_action_approval") {
    return (
      <Card tone="amber">
        <Heading
          icon={
            <FileCheck2
              size={16}
              className="text-amber-700 dark:text-amber-300"
            />
          }
        >
          {t("Approval requested")}
        </Heading>
        <p className="mt-1 text-xs leading-5 text-stone-600 dark:text-stone-300">
          {text(values.message) ||
            t("This action is waiting for approval in Integrations.")}
        </p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <div className="rounded-lg bg-white/70 px-2.5 py-2 dark:bg-black/10">
            <div className="text-[10px] font-semibold text-stone-400 uppercase">
              {t("Action")}
            </div>
            <div className="mt-0.5 text-xs font-semibold">
              {titleCase(
                text(input.operation) ||
                  text(values.action) ||
                  "Business action",
              )}
            </div>
          </div>
          <div className="rounded-lg bg-white/70 px-2.5 py-2 dark:bg-black/10">
            <div className="text-[10px] font-semibold text-stone-400 uppercase">
              {t("Approval")}
            </div>
            <div className="mt-0.5 text-xs font-semibold">
              {text(values.approvalId) || t("Pending")}
            </div>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <ActionLink href="/integrations">
            {t("Review in Integrations")}
          </ActionLink>
          <CopyButton value={values.approvalId} label={t("Copy approval ID")} />
        </div>
      </Card>
    );
  }

  if (name === "personalize_lumen") {
    const operation = text(input.operation);
    const action = text(values.action) || text(input.action);
    const setupUrl = text(values.setupUrl);
    const applied = values.applied === true;
    const outcome = text(values.outcome);
    const needsCorrection = ["conflict", "invalid_input"].includes(outcome);
    const needsReview = ["awaiting_user", "requires_admin"].includes(outcome);
    const tone = needsCorrection
      ? "amber"
      : needsReview || operation === "plan"
        ? "violet"
        : applied
          ? "emerald"
          : "stone";
    if (operation === "cancel") {
      return (
        <Card tone="stone">
          <Heading icon={<CircleAlert size={16} className="text-stone-500" />}>
            {t("Setup request cancelled")}
          </Heading>
        </Card>
      );
    }
    if (operation === "discover") {
      const groups = Object.entries(values).filter(([, value]) =>
        Array.isArray(value),
      );
      return (
        <Card tone="violet">
          <Heading
            icon={
              <Settings2
                size={16}
                className="text-violet-700 dark:text-violet-300"
              />
            }
          >
            {t("Available personalization actions")}
          </Heading>
          {groups.length ? (
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {groups.map(([key, value]) => (
                <div
                  key={key}
                  className="rounded-xl bg-white/70 px-3 py-2.5 dark:bg-black/10"
                >
                  <div className="text-xs font-semibold">{titleCase(key)}</div>
                  <div className="mt-1 text-[11px] text-stone-500">
                    {t("{{count}} options", {
                      count: Array.isArray(value) ? value.length : 0,
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <GenericResult value={result} />
          )}
        </Card>
      );
    }
    return (
      <Card tone={tone}>
        <Heading
          icon={
            <Settings2
              size={16}
              className="text-violet-700 dark:text-violet-300"
            />
          }
        >
          {needsCorrection
            ? t("Personalization needs attention")
            : needsReview
              ? t("Review personalization proposal")
              : outcome === "unchanged"
                ? t("Skill already saved")
                : operation === "plan"
                  ? t("Setup plan created")
                  : operation === "apply"
                    ? t("Personalization updated")
                    : operation === "status"
                      ? t("Setup request status")
                      : titleCase(operation || "Personalization")}
        </Heading>
        {action && (
          <p className="mt-1 text-xs font-semibold text-stone-700 dark:text-stone-200">
            {titleCase(action)}
          </p>
        )}
        {text(values.message) && (
          <p className="mt-1 text-xs leading-5 text-stone-600 dark:text-stone-300">
            {text(values.message)}
          </p>
        )}
        {needsCorrection && Array.isArray(values.issues) && (
          <ul
            role="alert"
            className="mt-2 list-inside list-disc text-xs text-amber-800 dark:text-amber-200"
          >
            {values.issues.map((issue, index) => {
              const entry = asRecord(issue);
              return (
                <li key={index}>
                  {Array.isArray(entry?.path)
                    ? entry.path.join(".") + ": "
                    : ""}
                  {text(entry?.message)}
                </li>
              );
            })}
          </ul>
        )}
        {["create_personal_skill", "create_executable_skill"].includes(
          action,
        ) &&
          text(values.id) && (
            <SkillProposalReview requestId={text(values.id)} />
          )}
        {setupUrl && (
          <div className="mt-3">
            <ActionLink href={setupUrl}>{t("Open Personalization")}</ActionLink>
          </div>
        )}
        {!setupUrl && values.applied === true && (
          <div className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 size={14} />
            {outcome === "unchanged"
              ? t("No changes")
              : t("Changes applied successfully")}
          </div>
        )}
      </Card>
    );
  }

  if (name === "publish_marketing_content") {
    const message = text(input.message) || text(input.caption);
    const mediaUrl = text(input.mediaUrl);
    return (
      <Card tone="sky">
        <Heading
          icon={<Send size={16} className="text-sky-700 dark:text-sky-300" />}
        >
          {status === "completed"
            ? t("Published successfully")
            : t("Ready to publish")}
        </Heading>
        <div className="mt-3 overflow-hidden rounded-xl border border-sky-200 bg-white dark:border-sky-900/60 dark:bg-stone-950/40">
          {mediaUrl && (
            <img
              src={mediaUrl}
              alt=""
              className="max-h-56 w-full object-cover"
            />
          )}
          {message && (
            <p className="p-3 text-sm leading-6 text-stone-700 dark:text-stone-200">
              {message}
            </p>
          )}
        </div>
        <div className="mt-2 flex items-center gap-2 text-xs text-stone-500">
          <CircleAlert size={13} />
          {status === "completed"
            ? t("The content was sent to the selected channel.")
            : t("Content was approved for publishing.")}
        </div>
      </Card>
    );
  }

  if (name === "send_marketing_email") {
    const recipients = input.to ?? input.recipients;
    const recipientLabel = Array.isArray(recipients)
      ? `${recipients.length} recipients`
      : text(recipients) || t("Subscribed audience");
    return (
      <Card tone="sky">
        <Heading
          icon={<Mail size={16} className="text-sky-700 dark:text-sky-300" />}
        >
          {status === "completed"
            ? t("Marketing email sent")
            : t("Marketing email prepared")}
        </Heading>
        <div className="mt-3 rounded-xl border border-sky-200 bg-white p-3 dark:border-sky-900/60 dark:bg-stone-950/40">
          <div className="text-[10px] font-semibold text-stone-400 uppercase">
            {t("To")}
          </div>
          <div className="text-xs font-semibold">{recipientLabel}</div>
          <div className="mt-2 text-[10px] font-semibold text-stone-400 uppercase">
            {t("Subject")}
          </div>
          <div className="text-sm font-semibold">
            {text(input.subject) || t("Marketing update")}
          </div>
          {text(input.message) && (
            <p className="mt-3 text-sm leading-6 text-stone-600 dark:text-stone-300">
              {text(input.message)}
            </p>
          )}
        </div>
      </Card>
    );
  }

  if (name === "run_marketing_report") {
    const reportEntries = Object.entries(values).filter(
      ([, value]) => typeof value === "number" || typeof value === "string",
    );
    return (
      <Card tone="violet">
        <Heading
          icon={
            <BarChart3
              size={16}
              className="text-violet-700 dark:text-violet-300"
            />
          }
        >
          {t("Marketing report")}
        </Heading>
        {reportEntries.length ? (
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {reportEntries.slice(0, 8).map(([key, value]) => (
              <div
                key={key}
                className="rounded-lg bg-white/70 px-2.5 py-2 dark:bg-black/10"
              >
                <div className="text-[10px] font-semibold text-stone-400 uppercase">
                  {titleCase(key)}
                </div>
                <div className="mt-0.5 text-lg font-bold">{String(value)}</div>
              </div>
            ))}
          </div>
        ) : (
          <GenericResult value={result} />
        )}
      </Card>
    );
  }

  if (name === "manage_marketing_lead") {
    return (
      <Card tone="emerald">
        <Heading
          icon={
            <UserRound
              size={16}
              className="text-emerald-700 dark:text-emerald-300"
            />
          }
        >
          {t("Marketing lead updated")}
        </Heading>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {Object.entries(values)
            .slice(0, 6)
            .map(([key, value]) => (
              <div
                key={key}
                className="rounded-lg bg-white/70 px-2.5 py-2 dark:bg-black/10"
              >
                <div className="text-[10px] font-semibold text-stone-400 uppercase">
                  {titleCase(key)}
                </div>
                <div className="mt-0.5 truncate text-xs font-semibold">
                  {typeof value === "object"
                    ? JSON.stringify(value)
                    : String(value)}
                </div>
              </div>
            ))}
        </div>
      </Card>
    );
  }

  if (name === "manage_business_integration") {
    const entries = Array.isArray(values.items)
      ? values.items
      : Array.isArray(values.results)
        ? values.results
        : Array.isArray(result)
          ? result
          : [];
    if (entries.length) {
      return (
        <BusinessRows operation={text(input.operation)} entries={entries} />
      );
    }
  }

  return <GenericResult value={result} />;
}
